import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import './v2-tokens.css';

export type V2Theme = 'light' | 'dark' | 'system' | 'high-contrast' | 'high-contrast-light';
export type V2ResolvedTheme = 'light' | 'dark' | 'high-contrast' | 'high-contrast-light';

interface V2ThemeContextValue {
    theme: V2Theme;
    resolvedTheme: V2ResolvedTheme;
    setTheme: (t: V2Theme) => void;
    toggleTheme: () => void;
    isDark: boolean;
    isHighContrast: boolean;
}

const V2ThemeContext = createContext<V2ThemeContextValue>({
    theme: 'system',
    resolvedTheme: 'dark',
    setTheme: () => { },
    toggleTheme: () => { },
    isDark: true,
    isHighContrast: false,
});

const STORAGE_KEY = 'theme-preference';
const VALID_THEMES: V2Theme[] = ['light', 'dark', 'system', 'high-contrast', 'high-contrast-light'];

function getSystemTheme(): V2ResolvedTheme {
    if (typeof window === 'undefined') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: V2Theme): V2ResolvedTheme {
    if (theme === 'system') return getSystemTheme();
    return theme;
}

function isDarkVariant(resolved: V2ResolvedTheme): boolean {
    return resolved === 'dark' || resolved === 'high-contrast';
}

function isHighContrastVariant(resolved: V2ResolvedTheme): boolean {
    return resolved === 'high-contrast' || resolved === 'high-contrast-light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode; defaultTheme?: V2Theme }> = ({
    children,
    defaultTheme,
}) => {
    const [theme, setThemeState] = useState<V2Theme>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY) as V2Theme | null;
            if (stored && VALID_THEMES.includes(stored)) return stored;
        } catch {
            /* empty */
        }
        // Default to clinical light theme — clean white hospital UI.
        // Users can still toggle to dark via the theme switcher.
        return defaultTheme ?? 'light';
    });

    const [resolvedTheme, setResolvedTheme] = useState<V2ResolvedTheme>(() => resolveTheme(theme));

    // Helper: apply resolved theme to DOM immediately (synchronous)
    const applyThemeToDOM = useCallback((resolved: V2ResolvedTheme) => {
        const root = document.documentElement;
        root.setAttribute('data-theme', resolved);
        if (isDarkVariant(resolved)) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, []);

    // Keep resolvedTheme state in sync (for consumers using context values)
    useEffect(() => {
        const resolved = resolveTheme(theme);
        setResolvedTheme(resolved);
        // DOM already updated synchronously in setTheme; this is a no-op in
        // most cases but covers cold-mount and hot-reload scenarios.
        applyThemeToDOM(resolved);
    }, [theme, applyThemeToDOM]);

    // System theme listener
    useEffect(() => {
        if (theme !== 'system') return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
            const resolved = getSystemTheme();
            setResolvedTheme(resolved);
            applyThemeToDOM(resolved);
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [theme, applyThemeToDOM]);

    const setTheme = useCallback((t: V2Theme) => {
        const root = document.documentElement;

        // 1. Enable CSS transitions BEFORE the theme attributes change so the
        //    browser paints the old colours with the transition rule already
        //    active — avoiding any snap/flicker on the first frame.
        root.classList.add('v2-theme-transitioning');

        // 2. Resolve and apply the new theme to the DOM SYNCHRONOUSLY so CSS
        //    vars and Tailwind's `.dark` class flip immediately — no React
        //    render-cycle latency.
        const resolved = resolveTheme(t);
        applyThemeToDOM(resolved);

        // 3. Persist and trigger re-render (updates context consumers like
        //    the logo swap in Header).
        setThemeState(t);
        try {
            localStorage.setItem(STORAGE_KEY, t);
        } catch {
            /* empty */
        }

        // 4. Remove the transition class once the longest animation (380 ms)
        //    finishes.  Use 400 ms to give a tiny grace margin.
        setTimeout(() => root.classList.remove('v2-theme-transitioning'), 400);
    }, [applyThemeToDOM]);

    const toggleTheme = useCallback(() => {
        const cycle: Record<V2Theme, V2Theme> = {
            system: 'light',
            light: 'dark',
            dark: 'system',
            'high-contrast': 'high-contrast-light',
            'high-contrast-light': 'high-contrast',
        };
        setTheme(cycle[theme] ?? 'system');
    }, [theme, setTheme]);

    const value: V2ThemeContextValue = {
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme,
        isDark: isDarkVariant(resolvedTheme),
        isHighContrast: isHighContrastVariant(resolvedTheme),
    };

    return (
        <V2ThemeContext.Provider value={value}>
            {children}
        </V2ThemeContext.Provider>
    );
};

export const useV2Theme = () => useContext(V2ThemeContext);

export default ThemeProvider;
